import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { CiSearch } from "react-icons/ci";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { RiAuctionLine } from "react-icons/ri";
import CustomDatePicker from "@/components/CustomDatePicker";
// import ValueRangeSlider from "../margin-range-slider";
import {
  AuctionProposalStatus,
  CreditAuctionProposalFilters,
} from "@/models/credit-auction-proposal.model";
import {
  NEGOTIATION_SUB_STEP_LABELS,
  NEGOTIATION_SUB_STEP_ORDER,
} from "@/utils/auctionProposalStatus";

interface CreditAuctionProposalsHeaderProps {
  minRangeLimit: number;
  maxRangeLimit: number;
  searchTerm: string;
  filters: CreditAuctionProposalFilters;
  dateRange: [Date | null, Date | null];
  validadeDateRange: [Date | null, Date | null];
  totalItems?: number;
  onSearchChange: (value: string) => void;
  onFilterChange: (field: keyof CreditAuctionProposalFilters, value: string | boolean | null) => void;
  onDateRangeChange: (dates: [Date | null, Date | null]) => void;
  onValidadeDateRangeChange: (dates: [Date | null, Date | null]) => void;
  onReleasedValueRangeChange: (range: { min: number; max: number }) => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
  /**
   * Quando true, não renderiza o container colorido externo — para uso
   * dentro de uma barra unificada (título + TabList + filtros).
   */
  embedded?: boolean;
}

const STATUS_OPTIONS: { label: string; value: AuctionProposalStatus }[] = [
  { label: "Leilão vencido BMP", value: AuctionProposalStatus.CHOSEN },
  { label: "Início Contratação", value: AuctionProposalStatus.PENDING_CONFIRMATION },
  { label: "Aguard. Formalização", value: AuctionProposalStatus.PENDING_FORMALIZATION },
  { label: "Pago", value: AuctionProposalStatus.PAYED },
  { label: "Leilão perdido", value: AuctionProposalStatus.REPROVED },
  { label: "Reprovado no processamento", value: AuctionProposalStatus.REPROVED_IN_PROCESS },
  { label: "Aguard. Análise Bancária", value: AuctionProposalStatus.PENDING_BANK_ANALYSIS },
  { label: "Processando simulação", value: AuctionProposalStatus.PROCESSING_SIMULATION },
  { label: "Aguardando escolha do cliente", value: AuctionProposalStatus.AWAITING_BORROWER_CHOICE },
  { label: "Aguard. Revisão", value: AuctionProposalStatus.PENDING_USER_REVIEW },
  { label: "Aguardando Validade Leilão", value: AuctionProposalStatus.AGUARDANDO_VALIDADE_LEILAO}
];

// "Progresso leilão" = propostas que venceram o leilão e estão EM ANDAMENTO na
// contratação. Inclui os status pós-vitória em progresso, para a busca por CPF
// encontrar o tomador onde ele parou. Ficam de fora: a fase de leilão (CREATED/
// IN_AUCTION), o perdido (REPROVED), o SELFSERVICE e os terminais/parados —
// PAYED, cancelamentos e reformalização não aparecem nesta aba.
const WON_AUCTION_STATUSES: AuctionProposalStatus[] = [
  AuctionProposalStatus.CHOSEN,
  AuctionProposalStatus.PENDING_CONFIRMATION,
  AuctionProposalStatus.PROCESSING_SIMULATION,
  AuctionProposalStatus.AWAITING_BORROWER_CHOICE,
  AuctionProposalStatus.PENDING_BANK_ANALYSIS,
  AuctionProposalStatus.PENDING_FORMALIZATION,
  AuctionProposalStatus.PENDING_USER_REVIEW,
  AuctionProposalStatus.REPROVED_IN_PROCESS,
];

export default function CreditAuctionProposalsHeader({
  // minRangeLimit,
  // maxRangeLimit,
  searchTerm,
  filters,
  dateRange,
  validadeDateRange,
  // totalItems,
  onSearchChange,
  onFilterChange,
  onDateRangeChange,
  onValidadeDateRangeChange,
  // onReleasedValueRangeChange,
  onClearFilters,
  onApplyFilters,
  embedded = false,
}: CreditAuctionProposalsHeaderProps) {
  const backgroundStyle = useConditionalBackground();
  const isWonAuctionOnly = filters.wonAuction === true;
  const visibleStatusOptions = isWonAuctionOnly
    ? STATUS_OPTIONS.filter((statusOption) =>
        WON_AUCTION_STATUSES.includes(statusOption.value),
      )
    : STATUS_OPTIONS;

  const filtersRow = (
    <VStack align="stretch" spacing={6}>
    <Flex w="100%" gap={4} flexWrap="wrap" align="flex-end">
          <FormControl
            flex={{ base: "1 1 100%", md: "0 0 auto" }}
            w={{ base: "100%", md: "220px" }}
          >
            <FormLabel color="white" fontFamily="Lexend Deca" fontSize={{ base: "xs", md: "sm" }} fontWeight="normal">
              Busca geral
            </FormLabel>
            <Tooltip
              label="Pesquise pelo CPF do tomador"
              fontSize="sm"
            >
              <InputGroup>
                <InputLeftElement
                  pointerEvents="none"
                  h={{ base: "3.5rem", md: "3rem" }}
                >
                  <CiSearch color="gray" size="1.25rem" />
                </InputLeftElement>
                <Input
                  value={searchTerm}
                  onChange={(event) => onSearchChange(event.target.value)}
                  bg="white"
                  color="black"
                  _hover={{ bg: "white" }}
                  _focus={{ bg: "white" }}
                  fontFamily="Lexend Deca"
                  fontSize={{ base: "xs", md: "sm" }}
                  h={{ base: "3.5rem", md: "3rem" }}
                />
              </InputGroup>
            </Tooltip>
          </FormControl>

          <FormControl flex="1" minW={{ base: "100%", md: "70px" }}>
            <FormLabel color="white" fontFamily="Lexend Deca" fontSize={{ base: "xs", md: "sm" }} fontWeight="normal">
              Status
            </FormLabel>
            <Select
              value={filters.statuses.join(",")}
              onChange={(event) => onFilterChange("statuses", event.target.value)}
              bg="white"
              color="black"
              fontFamily="Lexend Deca"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="normal"
              h={{ base: "3.5rem", md: "3rem" }}
            >
              <option value="">Todos</option>
              {visibleStatusOptions.map((statusOption) => (
                <option key={statusOption.value} value={statusOption.value}>
                  {statusOption.label}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl flex="1" minW={{ base: "100%", md: "80px" }}>
            <FormLabel color="white" fontFamily="Lexend Deca" fontSize={{ base: "xs", md: "sm" }} fontWeight="normal">
              Progresso Contratação
            </FormLabel>
            <Select
              value={filters.negotiationSubStep ?? ""}
              onChange={(event) =>
                onFilterChange("negotiationSubStep", event.target.value || null)
              }
              bg="white"
              color="black"
              fontFamily="Lexend Deca"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="normal"
              h={{ base: "3.5rem", md: "3rem" }}
            >
              <option value="">Todos</option>
              {NEGOTIATION_SUB_STEP_ORDER.map((subStep) => (
                <option key={subStep} value={subStep}>
                  {NEGOTIATION_SUB_STEP_LABELS[subStep]}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl
            flex={{ base: "1 1 100%", md: "0 0 auto" }}
            w={{ base: "100%", md: "220px" }}
          >
            <FormLabel color="white" fontFamily="Lexend Deca" fontSize={{ base: "xs", md: "sm" }} fontWeight="normal">
              Criação
            </FormLabel>
            <CustomDatePicker
              dateRange={dateRange}
              onChange={onDateRangeChange}
              fontSize={{ base: "xs", md: "sm" }}
              compact
            />
          </FormControl>

          <FormControl
            flex={{ base: "1 1 100%", md: "0 0 auto" }}
            w={{ base: "100%", md: "220px" }}
          >
            <FormLabel color="white" fontFamily="Lexend Deca" fontSize={{ base: "xs", md: "sm" }} fontWeight="normal">
              Validade
            </FormLabel>
            <CustomDatePicker
              dateRange={validadeDateRange}
              onChange={onValidadeDateRangeChange}
              fontSize={{ base: "xs", md: "sm" }}
              compact
            />
          </FormControl>

          <Flex
            gap={3}
            flexWrap="wrap"
            flex={{ base: "1 1 100%", md: "0 0 auto" }}
            alignSelf="flex-end"
          >
            <Button
              onClick={onApplyFilters}
              bg="white"
              color="teal.700"
              _hover={{ bg: "whiteAlpha.900" }}
              fontFamily="Lexend Deca"
              fontSize="10px"
              h={{ base: "3.5rem", md: "3rem" }}
            >
              Filtrar
            </Button>
            <Button
              onClick={onClearFilters}
              bg="whiteAlpha.200"
              color="white"
              border="1px solid"
              borderColor="whiteAlpha.400"
              _hover={{ bg: "whiteAlpha.300" }}
              fontFamily="Lexend Deca"
              fontSize="10px"
              h={{ base: "3.5rem", md: "3rem" }}
              whiteSpace="normal"
              lineHeight="1.2"
            >
              Limpar
              <br />
              filtros
            </Button>
          </Flex>
        </Flex>
    </VStack>
  );

  if (embedded) {
    return filtersRow;
  }

  return (
    <Box
      {...backgroundStyle}
      borderRadius="md"
      p={{ base: 4, md: 6 }}
      mb={6}
      color="white"
      boxShadow="md"
    >
      <VStack align="stretch" spacing={6}>
        <Flex alignItems="center" gap={4}>
          <Flex
            alignItems="center"
            justifyContent="center"
            w="54px"
            h="54px"
            borderRadius="lg"
            bg="whiteAlpha.200"
          >
            <Icon as={RiAuctionLine} boxSize={8} />
          </Flex>
          <Box>
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              fontFamily="Lexend Deca"
            >
              Propostas Leilão CLT
            </Text>
            <Text
              fontSize={{ base: "lg", md: "2xl" }}
              fontFamily="Lexend Deca"
            >
              Propostas Em Andamento
            </Text>
          </Box>
        </Flex>

        {filtersRow}
      </VStack>
    </Box>
  );
}
